<template>
  <div v-if="loaded">
    <div class="bg-slate-900 py-4 px-8 flex items-center justify-between flex-wrap">
      <div class="flex items-center justify-center text-white">
        <Logo />
      </div>

      <div class="flex justify-between gap-8">
        <div class="text-sm text-slate-300">
          Step {{ stepNumber + 1 }} of {{ steps.length }}{{ currentStep?.label ? ' - ' + currentStep.label : '' }}
        </div>
        <div class="flex items-center justify-between gap-6 text-white">
          <StepIndicator
            v-for="(step, index) in steps"
            :key="`stepIndicator.${index}`"
            :current-step-number="stepNumber"
            :step-number="index"
          />
        </div>
      </div>
    </div>
    <div class="py-24 px-6 max-w-3xl mx-auto text-center">
      <component
        :is="currentStep.component"
        :existing-data="onboardingData"
        @go-forward="handleStepForward"
        @go-back="handleStepBack"
        @register="handleRegistration"
      />
    </div>
  </div>
  <div v-else class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <div class="spinner mx-auto mb-4"></div>
      <div class="text-gray-600">Loading...</div>
    </div>
  </div>
  <div class="absolute bottom-8 left-8">
    <SignOutButton />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Logo from '../components/hummer/onboarding/Navbar/Logo.vue';
import StepIndicator from '../components/onboarding/StepIndicator.vue';
import CompanyStep from '../components/onboarding/steps/Company.vue';
import ConnectCalendarStep from '../components/onboarding/steps/ConnectCalendar.vue';
import FinalStep from '../components/onboarding/steps/Final.vue';
import ProfileStep from '../components/onboarding/steps/Profile.vue';
import CustomerInvite from '../components/onboarding/steps/CustomerInvite.vue';
import BillingStep from '../components/onboarding/steps/BillingStep.vue';
import { useCurrentUserStore } from '@/stores/currentUser.ts';
import SignOutButton from '@/components/shared/SignOutButton.vue';

const currentUserStore = useCurrentUserStore();
const route = useRoute();

const loaded = ref(false);
const onboardingData = ref({});
const stepNumber = ref(0);

const currentUserAccountDetails = computed(() => currentUserStore.currentUserAccountDetails);

function getInviteFlowSteps() {
  const steps = [
    {
      name: 'invite',
      label: 'Team Invitation',
      component: CustomerInvite,
    },
    {
      name: 'profile',
      label: 'Profile',
      component: ProfileStep,
    },
    {
      name: 'billing',
      label: 'Billing',
      component: BillingStep,
    },
    {
      name: 'calendar',
      label: 'Connect Calendar',
      component: ConnectCalendarStep,
    },
    {
      name: 'final',
      label: 'Onboarding Complete!',
      component: FinalStep,
    },
  ];

  if (process.env.NODE_ENV === 'test' || window.Cypress) {
    return steps.filter(step => step.name !== 'billing');
  }

  return steps;
}

function getTeamOwnerSteps() {
  return [
    {
      name: 'profile',
      label: 'Profile',
      component: ProfileStep,
    },
    {
      name: 'company',
      label: 'Company',
      component: CompanyStep,
    },
    {
      name: 'calendar',
      label: 'Connect Calendar',
      component: ConnectCalendarStep,
    },
    {
      name: 'final',
      label: 'Onboarding Complete!',
      component: FinalStep,
    },
  ];
}

function getDefaultSteps() {
  return [
    {
      name: 'profile',
      label: 'Profile',
      component: ProfileStep,
    },
    {
      name: 'calendar',
      label: 'Connect Calendar',
      component: ConnectCalendarStep,
    },
    {
      name: 'final',
      label: 'Onboarding Complete!',
      component: FinalStep,
    },
  ];
}

const steps = computed(() => {
  if (route.params.invite_token || currentUserAccountDetails.value?.useOnboardingInviteFlow) {
    return getInviteFlowSteps();
  } else if (currentUserAccountDetails.value?.onboardingPath === 'team_owner') {
    return getTeamOwnerSteps();
  } else {
    return getDefaultSteps();
  }
});

const currentStep = computed(() => {
  return steps.value[stepNumber.value];
});

async function handleRegistration(registrationData) {
  const result = await currentUserStore.registerUser({ registrationData: registrationData });

  if (result.success) {
    handleStepForward();
  }
}

function handleStepForward() {
  stepNumber.value += 1;
}

function handleStepBack() {
  stepNumber.value -= 1;
}

onMounted(async () => {
  try {
    await currentUserStore.loadCurrentUserAccountDetails();
    stepNumber.value = currentUserAccountDetails.value?.useOnboardingInviteFlow ? 3 : 0;
  } catch (error) {
    stepNumber.value = 0;
  } finally {
    document.body.classList.remove('lg:overflow-hidden');
    loaded.value = true;
  }
});
</script>

<style>
@reference "tailwindcss";
.spinner {
  height: 22px;
  width: 22px;
  animation: rotateSpinner 0.8s infinite linear;
  border: 2px solid black;
  border-right-color: transparent;
  border-radius: 50%;
}

@keyframes rotateSpinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

h2 {
  margin-top: 24px;
}

.formulate-input-element.formulate-input-element--text input,
.formulate-input-element.formulate-input-element--tel input,
.formulate-input-element.formulate-input-element--date input,
.formulate-input-element.formulate-input-element--select select {
  @apply rounded-lg border-2 border-gray-200 w-full max-w-xl;
}

.formulate-input-wrapper {
  @apply my-8;
}
</style>
